import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Database, RefreshCw, FlaskConical, CheckCircle2, XCircle, Clock,
  ChevronDown, ChevronRight, RotateCcw, FileDown, BellRing, BellOff,
  Square, GitCompare, Layers, AlertTriangle, Timer,
} from "lucide-react";

type BackoffCfg = { max_attempts?: number; strategy?: "exponential" | "linear" | "fixed"; base_ms?: number; max_ms?: number };
const BACKOFF_DEFAULTS = { max_attempts: 4, strategy: "exponential" as const, base_ms: 800, max_ms: 30_000 };

async function logAdminAction(action: string, details: Record<string, unknown>, target_table?: string) {
  try {
    await supabase.functions.invoke("log-admin-action", {
      body: { action, target_table: target_table ?? "kb_ingest_jobs", details },
    });
  } catch { /* non-blocking */ }
}

type ArticleRow = {
  id: string;
  slug: string;
  title: string;
  source: string | null;
  source_url: string | null;
  body: string;
  tags: string[] | null;
  chunks: number;
};

type JobRow = {
  id: string;
  mode: "run" | "dry_run";
  scope: string;
  status: "running" | "success" | "failed" | "partial" | "canceled";
  total_articles: number;
  processed: number;
  succeeded: number;
  failed: number;
  total_chunks: number;
  error: string | null;
  dry_run_report: unknown;
  started_at: string;
  finished_at: string | null;
  cancel_requested?: boolean;
  canceled_at?: string | null;
};

type ChunkPreview = { ordinal: number; chars: number; hash: string; preview: string };

type JobItem = {
  id: string;
  job_id: string;
  slug: string;
  title: string;
  status: "pending" | "running" | "success" | "failed" | "skipped";
  prev_chunks: number;
  chunks: number;
  attempts: number;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  prev_chunks_preview?: ChunkPreview[] | null;
  new_chunks_preview?: ChunkPreview[] | null;
  embedding_meta?: { model?: string; dims?: number; sample_vector?: number[] | null; embedded_count?: number } | null;
};

const STATUS_STYLES: Record<string, string> = {
  running: "text-primary",
  success: "text-emerald-500",
  failed: "text-destructive",
  partial: "text-amber-500",
  canceled: "text-muted-foreground",
  pending: "text-muted-foreground",
  skipped: "text-muted-foreground",
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] ?? "text-muted-foreground";
  const Icon =
    status === "success" ? CheckCircle2 :
    status === "failed" ? XCircle :
    status === "running" || status === "pending" ? Clock : CheckCircle2;
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-[11px] ${cls}`}>
      <Icon className={`size-3 ${status === "running" ? "animate-pulse" : ""}`} /> {status}
    </span>
  );
}

function downloadBlob(filename: string, mime: string, content: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function toCsv(rows: Array<Record<string, unknown>>): string {
  if (rows.length === 0) return "";
  const headers = Array.from(rows.reduce<Set<string>>((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
  const esc = (v: unknown) => {
    if (v == null) return "";
    const s = typeof v === "string" ? v : JSON.stringify(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
}

const NOTIFY_KEY = "kb-ingest-notify-enabled";

export function KbReindexPanel() {
  const [rows, setRows] = useState<ArticleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [activeJob, setActiveJob] = useState<JobRow | null>(null);
  const [activeItems, setActiveItems] = useState<JobItem[]>([]);
  const [recentJobs, setRecentJobs] = useState<JobRow[]>([]);
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<JobItem[]>([]);
  const [diffItem, setDiffItem] = useState<JobItem | null>(null);
  const [bulkN, setBulkN] = useState<number>(5);
  const [canceling, setCanceling] = useState(false);
  const [bulkScopeFilter, setBulkScopeFilter] = useState<string>("any");
  const [bulkModeFilter, setBulkModeFilter] = useState<"run" | "dry_run" | "any">("any");
  const [bulkTargetMode, setBulkTargetMode] = useState<"run" | "dry_run">("run");
  const [blocked, setBlocked] = useState<{ scope: string; activeJobId: string } | null>(null);
  const [notifyEnabled, setNotifyEnabled] = useState<boolean>(() =>
    typeof window === "undefined" ? false : localStorage.getItem(NOTIFY_KEY) === "1"
  );
  const [defaultMaxAttempts, setDefaultMaxAttempts] = useState<number>(BACKOFF_DEFAULTS.max_attempts);
  const [perScopeBackoff, setPerScopeBackoff] = useState<Record<string, BackoffCfg>>({});
  const [confirmRetryFor, setConfirmRetryFor] = useState<{ jobId: string; jobScope: string; jobMode: string } | null>(null);
  const rowsRef = useRef<ArticleRow[]>([]);
  rowsRef.current = rows;

  const effectiveBackoff = (scope: string): Required<BackoffCfg> => {
    const cfg = perScopeBackoff[scope] ?? {};
    return {
      max_attempts: cfg.max_attempts ?? defaultMaxAttempts ?? BACKOFF_DEFAULTS.max_attempts,
      strategy: cfg.strategy ?? BACKOFF_DEFAULTS.strategy,
      base_ms: cfg.base_ms ?? BACKOFF_DEFAULTS.base_ms,
      max_ms: cfg.max_ms ?? BACKOFF_DEFAULTS.max_ms,
    };
  };

  const loadSettings = async () => {
    const { data } = await supabase
      .from("kb_ingest_settings")
      .select("max_attempts, per_scope_retry_backoff")
      .maybeSingle();
    if (data) {
      const d = data as { max_attempts?: number; per_scope_retry_backoff?: Record<string, BackoffCfg> };
      setDefaultMaxAttempts(d.max_attempts ?? BACKOFF_DEFAULTS.max_attempts);
      setPerScopeBackoff(d.per_scope_retry_backoff ?? {});
    }
  };

  const loadArticles = async () => {
    setLoading(true);
    const { data: articles, error } = await supabase
      .from("kb_articles")
      .select("id, slug, title, source, source_url, body, tags")
      .eq("status", "published")
      .order("created_at", { ascending: true });
    if (error) { setLoading(false); toast.error(error.message); return; }
    const ids = (articles ?? []).map((a) => a.id);
    const counts = new Map<string, number>();
    if (ids.length > 0) {
      const { data: chunks } = await supabase.from("kb_chunks").select("article_id").in("article_id", ids);
      for (const c of (chunks ?? []) as { article_id: string }[]) counts.set(c.article_id, (counts.get(c.article_id) ?? 0) + 1);
    }
    setRows((articles ?? []).map((a) => ({ ...(a as Omit<ArticleRow, "chunks">), chunks: counts.get(a.id) ?? 0 })));
    setLoading(false);
  };

  const loadRecentJobs = async () => {
    const { data } = await supabase.from("kb_ingest_jobs").select("*").order("started_at", { ascending: false }).limit(10);
    setRecentJobs((data ?? []) as JobRow[]);
  };

  useEffect(() => { loadArticles(); loadRecentJobs(); loadSettings(); }, []);

  const notifyCompleted = (job: JobRow) => {
    const label = job.mode === "dry_run" ? "Dry run" : "Reindex";
    const summary =
      job.status === "success" ? `${label} complete — ${job.total_chunks} chunks`
      : job.status === "canceled" ? `${label} canceled`
      : job.status === "partial" ? `${label} finished with ${job.failed} failure(s)`
      : `${label} failed${job.error ? `: ${job.error}` : ""}`;
    const viewDetails = () => loadRecentJobs().then(() => {
      setExpandedJob(job.id);
      loadExpanded(job.id, true);
      document.getElementById(`kb-job-${job.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
    if (job.status === "success") toast.success(summary, { action: { label: "View details", onClick: viewDetails } });
    else if (job.status === "partial") toast.warning(summary, { action: { label: "View details", onClick: viewDetails } });
    else if (job.status === "canceled") toast.info(summary, { action: { label: "View details", onClick: viewDetails } });
    else toast.error(summary, { action: { label: "View details", onClick: viewDetails } });

    if (notifyEnabled && typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      const n = new Notification(`KB ${label} ${job.status}`, {
        body: `${job.succeeded}/${job.total_articles} ok · ${job.total_chunks} chunks`,
        tag: `kb-job-${job.id}`,
      });
      n.onclick = () => { window.focus(); viewDetails(); n.close(); };
    }
  };

  useEffect(() => {
    if (!activeJobId) return;
    let cancelled = false;
    (async () => {
      const [{ data: job }, { data: items }] = await Promise.all([
        supabase.from("kb_ingest_jobs").select("*").eq("id", activeJobId).maybeSingle(),
        supabase.from("kb_ingest_job_items").select("*").eq("job_id", activeJobId).order("created_at"),
      ]);
      if (cancelled) return;
      if (job) setActiveJob(job as JobRow);
      if (items) setActiveItems(items as unknown as JobItem[]);
    })();

    const channel = supabase
      .channel(`kb-job-${activeJobId}:${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "kb_ingest_jobs", filter: `id=eq.${activeJobId}` },
        (payload) => {
          const next = payload.new as unknown as JobRow;
          setActiveJob(next);
          if (next.status !== "running" && next.finished_at) {
            loadArticles(); loadRecentJobs(); notifyCompleted(next); setBusy(false); setCanceling(false);
          }
        })
      .on("postgres_changes",
        { event: "*", schema: "public", table: "kb_ingest_job_items", filter: `job_id=eq.${activeJobId}` },
        (payload) => {
          const next = payload.new as unknown as JobItem;
          setActiveItems((prev) => {
            const idx = prev.findIndex((p) => p.id === next.id);
            if (idx === -1) return [...prev, next];
            const copy = [...prev]; copy[idx] = next; return copy;
          });
        })
      .subscribe();

    return () => { cancelled = true; supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJobId, notifyEnabled]);

  const startJob = async (subset: ArticleRow[], mode: "run" | "dry_run", scope: string) => {
    if (subset.length === 0) return;
    setBusy(true); setActiveJob(null); setActiveItems([]); setBlocked(null);
    try {
      const { data, error } = await supabase.functions.invoke("kb-ingest", {
        body: {
          mode, scope,
          articles: subset.map((a) => ({
            title: a.title, slug: a.slug, source: a.source ?? "manual",
            source_url: a.source_url, body: a.body, tags: a.tags ?? [],
          })),
        },
      });
      const blockedResp = (data as { blocked?: boolean; active_job_id?: string; scope?: string; reason?: string }) ?? null;
      if (blockedResp?.blocked) {
        setBlocked({ scope: blockedResp.scope ?? scope, activeJobId: blockedResp.active_job_id ?? "" });
        toast.warning(`Blocked: ${blockedResp.reason ?? "active job exists"} in scope “${blockedResp.scope ?? scope}”`);
        setBusy(false);
        return;
      }
      if (error) throw error;
      const jobId = (data as { job_id?: string })?.job_id;
      if (!jobId) throw new Error("no job_id returned");
      setActiveJobId(jobId);
      toast.info(`${mode === "dry_run" ? "Dry run" : "Reindex"} started (${subset.length} articles)`);
      loadRecentJobs();
    } catch (e) {
      setBusy(false);
      toast.error(e instanceof Error ? e.message : "Failed to start job");
    }
  };

  const cancelActive = async () => {
    if (!activeJob || activeJob.status !== "running") return;
    setCanceling(true);
    const { error } = await supabase
      .from("kb_ingest_jobs")
      .update({ cancel_requested: true })
      .eq("id", activeJob.id);
    if (error) { setCanceling(false); toast.error(error.message); return; }
    toast.info("Cancel requested — workers will stop after the current article.");
  };

  const retryFailedFromJob = async (jobId: string, mode: "run" | "dry_run") => {
    const { data: items } = await supabase.from("kb_ingest_job_items")
      .select("slug").eq("job_id", jobId).eq("status", "failed");
    const failedSlugs = new Set((items ?? []).map((i) => i.slug));
    const subset = rowsRef.current.filter((r) => failedSlugs.has(r.slug));
    if (subset.length === 0) { toast.info("No matching failed articles remain to retry."); return; }
    await startJob(subset, mode, "retry");
  };

  // Retry only the failed items shown in the expanded view, optionally filtered by scope/mode of the source job.
  const retryFailedFromExpanded = async (
    jobId: string,
    opts: { scopeFilter?: string; modeFilter?: "run" | "dry_run" | "any"; targetMode: "run" | "dry_run" },
  ) => {
    let q = supabase.from("kb_ingest_job_items").select("slug, job_id").eq("job_id", jobId).eq("status", "failed");
    // If scope/mode filters are set, narrow via parent jobs (here we already constrained to this job_id, so filters effectively gate whether we run at all).
    if (opts.scopeFilter && opts.scopeFilter !== "any") {
      const { data: parent } = await supabase.from("kb_ingest_jobs").select("scope, mode").eq("id", jobId).maybeSingle();
      if (parent && parent.scope !== opts.scopeFilter) {
        toast.info(`Job scope "${parent.scope}" does not match filter "${opts.scopeFilter}".`); return;
      }
      if (opts.modeFilter && opts.modeFilter !== "any" && parent && parent.mode !== opts.modeFilter) {
        toast.info(`Job mode "${parent.mode}" does not match filter "${opts.modeFilter}".`); return;
      }
    }
    const { data: items } = await q;
    const slugs = new Set((items ?? []).map((i) => i.slug));
    const subset = rowsRef.current.filter((r) => slugs.has(r.slug));
    if (subset.length === 0) { toast.info("No matching failed items to retry."); return; }
    await logAdminAction("kb_bulk_retry_filtered", {
      source_job_id: jobId,
      filters: { scope: opts.scopeFilter ?? "any", mode: opts.modeFilter ?? "any" },
      target_mode: opts.targetMode,
      item_count: subset.length,
      triggered_at: new Date().toISOString(),
    });
    await startJob(subset, opts.targetMode, "retry");
  };

  const retryAllFailedAcross = async () => {
    const n = Math.max(1, Math.min(10, bulkN || 5));
    const { data: jobs } = await supabase
      .from("kb_ingest_jobs").select("id").order("started_at", { ascending: false }).limit(n);
    const jobIds = (jobs ?? []).map((j) => j.id);
    if (jobIds.length === 0) { toast.info("No jobs to scan."); return; }
    const { data: items } = await supabase
      .from("kb_ingest_job_items").select("slug").in("job_id", jobIds).eq("status", "failed");
    const failedSlugs = new Set((items ?? []).map((i) => i.slug));
    const subset = rowsRef.current.filter((r) => failedSlugs.has(r.slug));
    if (subset.length === 0) { toast.info(`No failed articles across the last ${n} jobs.`); return; }
    await logAdminAction("kb_bulk_retry_across_recent", {
      window_jobs: n,
      source_job_ids: jobIds,
      item_count: subset.length,
      triggered_at: new Date().toISOString(),
    });
    await startJob(subset, "run", "retry-bulk");
  };

  const retrySingle = async (slug: string) => {
    const a = rowsRef.current.find((r) => r.slug === slug);
    if (!a) { toast.error("Article no longer available locally."); return; }
    await startJob([a], "run", "single");
  };

  const exportJobReport = async (jobId: string, format: "csv" | "json") => {
    const [{ data: job }, { data: items }] = await Promise.all([
      supabase.from("kb_ingest_jobs").select("*").eq("id", jobId).maybeSingle(),
      supabase.from("kb_ingest_job_items").select("*").eq("job_id", jobId).order("created_at"),
    ]);
    if (!job) { toast.error("Job not found"); return; }
    const j = job as JobRow;
    const its = (items ?? []) as unknown as JobItem[];
    const durationMs = j.finished_at ? new Date(j.finished_at).getTime() - new Date(j.started_at).getTime() : null;
    if (format === "json") {
      downloadBlob(`kb-job-${jobId}.json`, "application/json", JSON.stringify({
        job: { ...j, duration_ms: durationMs },
        items: its.map((it) => ({
          ...it,
          duration_ms: it.started_at && it.finished_at ? new Date(it.finished_at).getTime() - new Date(it.started_at).getTime() : null,
          delta_chunks: it.chunks - it.prev_chunks,
        })),
        dry_run_report: j.dry_run_report ?? null,
      }, null, 2));
    } else {
      const csvRows = its.map((it) => ({
        slug: it.slug, title: it.title, status: it.status,
        prev_chunks: it.prev_chunks, new_chunks: it.chunks, delta_chunks: it.chunks - it.prev_chunks,
        attempts: it.attempts, started_at: it.started_at ?? "", finished_at: it.finished_at ?? "",
        duration_ms: it.started_at && it.finished_at ? new Date(it.finished_at).getTime() - new Date(it.started_at).getTime() : "",
        error: it.error ?? "",
      }));
      const header = `# job_id=${j.id} mode=${j.mode} scope=${j.scope} status=${j.status} processed=${j.processed}/${j.total_articles} chunks=${j.total_chunks} failed=${j.failed} duration_ms=${durationMs ?? ""}\n`;
      downloadBlob(`kb-job-${jobId}.csv`, "text/csv", header + toCsv(csvRows));
    }
    toast.success(`Exported ${format.toUpperCase()}`);
  };

  // Items-only CSV (status, attempts, timestamps) for the Recent jobs row export.
  const exportJobItemsCsv = async (jobId: string) => {
    const { data: items } = await supabase
      .from("kb_ingest_job_items").select("*").eq("job_id", jobId).order("created_at");
    const its = (items ?? []) as unknown as JobItem[];
    if (its.length === 0) { toast.info("No items to export."); return; }
    const csvRows = its.map((it) => ({
      slug: it.slug,
      title: it.title,
      status: it.status,
      attempts: it.attempts,
      prev_chunks: it.prev_chunks,
      new_chunks: it.chunks,
      delta_chunks: it.chunks - it.prev_chunks,
      started_at: it.started_at ?? "",
      finished_at: it.finished_at ?? "",
      duration_ms: it.started_at && it.finished_at
        ? new Date(it.finished_at).getTime() - new Date(it.started_at).getTime() : "",
      error: it.error ?? "",
    }));
    downloadBlob(`kb-job-${jobId}-items.csv`, "text/csv", toCsv(csvRows));
    toast.success(`Exported ${its.length} item rows`);
  };


  const toggleNotifications = async () => {
    if (!notifyEnabled) {
      if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") toast.warning("Notifications blocked; toasts will still show.");
      }
      setNotifyEnabled(true); localStorage.setItem(NOTIFY_KEY, "1"); toast.success("Desktop notifications on");
    } else {
      setNotifyEnabled(false); localStorage.removeItem(NOTIFY_KEY); toast.info("Desktop notifications off");
    }
  };

  const loadExpanded = async (jobId: string, force = false) => {
    if (!force && expandedJob === jobId) { setExpandedJob(null); return; }
    setExpandedJob(jobId);
    const { data } = await supabase.from("kb_ingest_job_items").select("*").eq("job_id", jobId).order("created_at");
    setExpandedItems((data ?? []) as unknown as JobItem[]);
  };

  const openDiff = async (item: JobItem) => {
    // Fetch full row to ensure previews are present (list query may have been issued before they were written)
    const { data } = await supabase.from("kb_ingest_job_items").select("*").eq("id", item.id).maybeSingle();
    setDiffItem((data as unknown as JobItem) ?? item);
  };

  const unindexed = useMemo(() => rows.filter((r) => r.chunks === 0), [rows]);
  const totalChunks = useMemo(() => rows.reduce((n, r) => n + r.chunks, 0), [rows]);
  const progressPct = activeJob && activeJob.total_articles > 0
    ? Math.round((activeJob.processed / activeJob.total_articles) * 100) : 0;
  const liveByArticle = useMemo(() => {
    const m = new Map<string, JobItem>();
    for (const it of activeItems) m.set(it.slug, it);
    return m;
  }, [activeItems]);

  return (
    <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Database className="size-5 text-primary" />
          <div>
            <h2 className="font-display text-base font-bold">Knowledge base</h2>
            <p className="font-mono text-xs text-muted-foreground">
              {rows.length} articles · {totalChunks} chunks
              {unindexed.length > 0 && ` · ${unindexed.length} unindexed`}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={toggleNotifications} title="Toggle desktop notifications">
            {notifyEnabled ? <BellRing className="size-4 text-primary" /> : <BellOff className="size-4" />}
          </Button>
          <Button size="sm" variant="outline" onClick={() => startJob(rows, "dry_run", "all")} disabled={busy || rows.length === 0}>
            <FlaskConical className="size-4" /> Dry run
          </Button>
          {unindexed.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => startJob(unindexed, "run", "missing")} disabled={busy}>
              <RefreshCw className={`size-4 ${busy ? "animate-spin" : ""}`} />
              Index missing ({unindexed.length})
            </Button>
          )}
          <Button size="sm" onClick={() => startJob(rows, "run", "all")} disabled={busy || rows.length === 0}>
            <RefreshCw className={`size-4 ${busy ? "animate-spin" : ""}`} />
            Reindex all
          </Button>
        </div>
      </div>

      {/* Bulk retry across last N jobs */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Layers className="size-3.5 text-muted-foreground" />
        <span className="font-mono text-[11px] text-muted-foreground">Retry all failed items across last</span>
        <Input
          type="number" min={1} max={10}
          value={bulkN}
          onChange={(e) => setBulkN(Math.max(1, Math.min(10, Number(e.target.value || 5))))}
          className="h-7 w-16 text-xs"
        />
        <span className="font-mono text-[11px] text-muted-foreground">jobs</span>
        <Button size="sm" variant="outline" onClick={retryAllFailedAcross} disabled={busy}>
          <RotateCcw className="size-3.5" /> Run
        </Button>
      </div>

      {/* Blocked-job warning */}
      {blocked && (
        <div className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="size-4 text-amber-500 mt-0.5" />
            <div className="text-xs">
              <p className="font-semibold text-amber-600 dark:text-amber-400">Job blocked</p>
              <p className="font-mono text-muted-foreground">
                A job is already running in scope “{blocked.scope}”. Single-active-per-scope is enabled.
                {blocked.activeJobId && <> Active job: <span className="text-foreground">{blocked.activeJobId.slice(0, 8)}…</span></>}
              </p>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setBlocked(null)}>Dismiss</Button>
        </div>
      )}



      {/* Live progress */}
      {activeJob && (
        <div className="mt-4 rounded-lg border border-border bg-background/40 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <div className="flex items-center gap-2">
              <StatusBadge status={activeJob.status} />
              <span className="font-mono text-xs text-muted-foreground">
                {activeJob.mode === "dry_run" ? "dry run" : "reindex"} · {activeJob.scope}
                {activeJob.cancel_requested && activeJob.status === "running" && " · canceling…"}
              </span>
            </div>
            <span className="font-mono text-xs text-muted-foreground">
              {activeJob.processed}/{activeJob.total_articles} · {activeJob.total_chunks} chunks
              {activeJob.failed > 0 && ` · ${activeJob.failed} failed`}
            </span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div
              className={`h-full transition-all ${activeJob.status === "failed" ? "bg-destructive" : "bg-primary"}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {activeJob.status === "running" && (
              <Button size="sm" variant="destructive" onClick={cancelActive} disabled={canceling || !!activeJob.cancel_requested}>
                <Square className="size-3.5" /> {canceling || activeJob.cancel_requested ? "Canceling…" : "Cancel job"}
              </Button>
            )}
            {activeJob.status !== "running" && (
              <>
                <Button size="sm" variant="outline" onClick={() => exportJobReport(activeJob.id, "csv")}>
                  <FileDown className="size-3.5" /> Export CSV
                </Button>
                <Button size="sm" variant="outline" onClick={() => exportJobReport(activeJob.id, "json")}>
                  <FileDown className="size-3.5" /> Export JSON
                </Button>
                {activeJob.failed > 0 && (
                  <Button size="sm" variant="outline" onClick={() => retryFailedFromJob(activeJob.id, activeJob.mode)} disabled={busy}>
                    <RotateCcw className="size-3.5" /> Retry {activeJob.failed} failed
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading articles…</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">No published articles yet.</p>
      ) : (
        <ul className="mt-4 divide-y divide-border border-y border-border">
          {rows.map((a) => {
            const live = liveByArticle.get(a.slug);
            return (
              <li key={a.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <div className="min-w-0">
                  <div className="truncate font-medium">{a.title}</div>
                  <div className="font-mono text-[11px] text-muted-foreground truncate">{a.slug}</div>
                </div>
                <div className="flex items-center gap-3">
                  {live && live.status !== "pending" && <StatusBadge status={live.status} />}
                  <span className={`font-mono text-xs ${(live?.chunks ?? a.chunks) === 0 ? "text-destructive" : "text-muted-foreground"}`}>
                    {live?.chunks ?? a.chunks} chunks
                  </span>
                  {live && (live.new_chunks_preview || live.prev_chunks_preview) && (
                    <Button size="sm" variant="ghost" onClick={() => openDiff(live)} title="View chunk diff">
                      <GitCompare className="size-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => startJob([a], "run", "single")} disabled={busy}>
                    <RefreshCw className="size-3.5" /> Reindex
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Recent jobs */}
      <div className="mt-5">
        <h3 className="font-display text-sm font-bold mb-2">Recent jobs</h3>
        {recentJobs.length === 0 ? (
          <p className="text-xs text-muted-foreground">No jobs yet.</p>
        ) : (
          <ul className="divide-y divide-border border-y border-border">
            {recentJobs.map((j) => {
              const isOpen = expandedJob === j.id;
              const dur = j.finished_at
                ? Math.max(0, Math.round((new Date(j.finished_at).getTime() - new Date(j.started_at).getTime()) / 100) / 10)
                : null;
              return (
                <li key={j.id} id={`kb-job-${j.id}`} className="py-2 text-sm">
                  <div className="flex w-full items-center justify-between gap-3">
                    <button type="button" onClick={() => loadExpanded(j.id)} className="flex flex-1 items-center justify-between gap-3 text-left min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        {isOpen ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                        <StatusBadge status={j.status} />
                        <span className="font-mono text-[11px] text-muted-foreground">
                          {j.mode === "dry_run" ? "dry" : "run"} · {j.scope}
                        </span>
                      </div>
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {j.succeeded}/{j.total_articles} ok · {j.total_chunks} chunks
                        {j.failed > 0 && ` · ${j.failed} failed`}
                        {dur !== null && ` · ${dur}s`}
                        {" · "}
                        {new Date(j.started_at).toLocaleString()}
                      </span>
                    </button>
                    <Button
                      size="sm" variant="ghost" className="h-7 px-2"
                      title="Download per-article items CSV (status, attempts, timestamps)"
                      onClick={(e) => { e.stopPropagation(); exportJobItemsCsv(j.id); }}
                    >
                      <FileDown className="size-3.5" /> Items CSV
                    </Button>
                  </div>
                  {isOpen && (
                    <div className="mt-2 ml-5 rounded border border-border bg-background/40 p-2">
                      {j.error && <div className="mb-2 text-[11px] text-destructive font-mono">{j.error}</div>}
                      <div className="mb-2 flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => exportJobReport(j.id, "csv")}>
                          <FileDown className="size-3.5" /> CSV
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => exportJobReport(j.id, "json")}>
                          <FileDown className="size-3.5" /> JSON
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => exportJobItemsCsv(j.id)}>
                          <FileDown className="size-3.5" /> Items CSV
                        </Button>
                        {j.failed > 0 && (
                          <Button size="sm" variant="outline" onClick={() => retryFailedFromJob(j.id, j.mode)} disabled={busy}>
                            <RotateCcw className="size-3.5" /> Retry {j.failed} failed
                          </Button>
                        )}
                      </div>

                      {j.failed > 0 && (
                        <div className="mb-2 flex flex-wrap items-center gap-2 rounded border border-dashed border-border bg-background/30 p-2">
                          <span className="font-mono text-[10px] text-muted-foreground">Bulk retry failed (filter):</span>
                          <select
                            className="h-7 rounded-md border border-input bg-background px-2 text-[11px]"
                            value={bulkScopeFilter}
                            onChange={(e) => setBulkScopeFilter(e.target.value)}
                            title="Only run if job scope matches"
                          >
                            <option value="any">any scope</option>
                            <option value="all">all</option>
                            <option value="missing">missing</option>
                            <option value="single">single</option>
                            <option value="retry">retry</option>
                            <option value="retry-bulk">retry-bulk</option>
                            <option value="scheduled">scheduled</option>
                            <option value="test">test</option>
                            <option value="custom">custom</option>
                          </select>
                          <select
                            className="h-7 rounded-md border border-input bg-background px-2 text-[11px]"
                            value={bulkModeFilter}
                            onChange={(e) => setBulkModeFilter(e.target.value as "run" | "dry_run" | "any")}
                            title="Only run if job mode matches"
                          >
                            <option value="any">any mode</option>
                            <option value="run">run</option>
                            <option value="dry_run">dry_run</option>
                          </select>
                          <span className="font-mono text-[10px] text-muted-foreground">→ retry as</span>
                          <select
                            className="h-7 rounded-md border border-input bg-background px-2 text-[11px]"
                            value={bulkTargetMode}
                            onChange={(e) => setBulkTargetMode(e.target.value as "run" | "dry_run")}
                          >
                            <option value="run">run</option>
                            <option value="dry_run">dry_run</option>
                          </select>
                          <Button
                            size="sm" variant="outline" disabled={busy}
                            onClick={() => setConfirmRetryFor({ jobId: j.id, jobScope: j.scope, jobMode: j.mode })}
                          >
                            <RotateCcw className="size-3.5" /> Retry filtered failed…
                          </Button>
                        </div>
                      )}

                      {/* Effective per-scope retry backoff for this job */}
                      {(() => {
                        const eff = effectiveBackoff(j.scope);
                        const overridden = !!perScopeBackoff[j.scope];
                        return (
                          <div className="mb-2 flex flex-wrap items-center gap-2 rounded border border-border/60 bg-background/30 p-2 text-[10px] font-mono text-muted-foreground">
                            <Timer className="size-3" />
                            <span>backoff[{j.scope}]:</span>
                            <span>max_attempts=<span className="text-foreground">{eff.max_attempts}</span></span>
                            <span>strategy=<span className="text-foreground">{eff.strategy}</span></span>
                            <span>base_ms=<span className="text-foreground">{eff.base_ms}</span></span>
                            <span>max_ms=<span className="text-foreground">{eff.max_ms}</span></span>
                            <span className={overridden ? "text-primary" : "text-muted-foreground"}>
                              {overridden ? "(scope override)" : "(default)"}
                            </span>
                          </div>
                        );
                      })()}

                      {expandedItems.length === 0 ? (
                        <p className="text-[11px] text-muted-foreground">No items.</p>
                      ) : (
                        <table className="w-full text-[11px] font-mono">
                          <thead className="text-muted-foreground">
                            <tr>
                              <th className="text-left">slug</th>
                              <th>status</th>
                              <th className="text-right">prev→new</th>
                              <th className="text-right">attempts</th>
                              <th className="text-right">backoff</th>
                              <th className="text-left">error</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {expandedItems.map((it) => {
                              const eff = effectiveBackoff(j.scope);
                              return (
                                <tr key={it.id} className="border-t border-border/50">
                                  <td className="truncate max-w-[180px]">{it.slug}</td>
                                  <td><StatusBadge status={it.status} /></td>
                                  <td className="text-right">{it.prev_chunks}→{it.chunks}</td>
                                  <td className="text-right">{it.attempts}/{eff.max_attempts}</td>
                                  <td
                                    className="text-right text-muted-foreground"
                                    title={`strategy=${eff.strategy} base_ms=${eff.base_ms} max_ms=${eff.max_ms} max_attempts=${eff.max_attempts}`}
                                  >
                                    {eff.strategy.slice(0, 3)}·{eff.base_ms}→{eff.max_ms}
                                  </td>
                                  <td className="truncate max-w-[240px] text-destructive">{it.error ?? ""}</td>
                                  <td className="text-right whitespace-nowrap">
                                    <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => openDiff(it)} title="Diff">
                                      <GitCompare className="size-3" />
                                    </Button>
                                    {it.status === "failed" && (
                                      <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => retrySingle(it.slug)} disabled={busy}>
                                        <RotateCcw className="size-3" />
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ChunkDiffDialog item={diffItem} onClose={() => setDiffItem(null)} />

      <AlertDialog open={!!confirmRetryFor} onOpenChange={(o) => !o && setConfirmRetryFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retry filtered failed items?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>Verify the selected filters before triggering a bulk retry. A new job will be created.</p>
                {confirmRetryFor && (
                  <div className="rounded border border-border bg-muted/40 p-2 font-mono text-[11px]">
                    <div>source_job_id: <span className="text-foreground">{confirmRetryFor.jobId.slice(0, 8)}…</span></div>
                    <div>source job scope/mode: <span className="text-foreground">{confirmRetryFor.jobScope} / {confirmRetryFor.jobMode}</span></div>
                    <div>scope filter: <span className="text-foreground">{bulkScopeFilter}</span></div>
                    <div>mode filter: <span className="text-foreground">{bulkModeFilter}</span></div>
                    <div>target run type: <span className="text-foreground">{bulkTargetMode}</span></div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!confirmRetryFor) return;
                const target = confirmRetryFor;
                setConfirmRetryFor(null);
                retryFailedFromExpanded(target.jobId, {
                  scopeFilter: bulkScopeFilter, modeFilter: bulkModeFilter, targetMode: bulkTargetMode,
                });
              }}
            >
              Run retry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function ChunkDiffDialog({ item, onClose }: { item: JobItem | null; onClose: () => void }) {
  const prev = item?.prev_chunks_preview ?? [];
  const next = item?.new_chunks_preview ?? [];
  const maxLen = Math.max(prev.length, next.length);
  const rows = Array.from({ length: maxLen }, (_, i) => ({
    ordinal: i,
    p: prev[i] ?? null,
    n: next[i] ?? null,
  }));

  const exportDiff = (format: "json" | "csv") => {
    if (!item) return;
    const safeSlug = (item.slug || "item").replace(/[^a-z0-9-_]+/gi, "_");
    if (format === "json") {
      downloadBlob(`kb-diff-${safeSlug}.json`, "application/json", JSON.stringify({
        slug: item.slug, title: item.title, status: item.status,
        prev_chunks: item.prev_chunks, new_chunks: item.chunks,
        delta_chunks: item.chunks - item.prev_chunks,
        embedding_meta: item.embedding_meta ?? null,
        rows: rows.map((r) => ({
          ordinal: r.ordinal,
          changed: (r.p?.hash ?? "") !== (r.n?.hash ?? ""),
          prev: r.p, next: r.n,
        })),
      }, null, 2));
    } else {
      downloadBlob(`kb-diff-${safeSlug}.csv`, "text/csv", toCsv(rows.map((r) => ({
        ordinal: r.ordinal,
        changed: (r.p?.hash ?? "") !== (r.n?.hash ?? ""),
        prev_hash: r.p?.hash ?? "",
        prev_chars: r.p?.chars ?? "",
        prev_preview: r.p?.preview ?? "",
        new_hash: r.n?.hash ?? "",
        new_chars: r.n?.chars ?? "",
        new_preview: r.n?.preview ?? "",
        model: item.embedding_meta?.model ?? "",
        dims: item.embedding_meta?.dims ?? "",
      }))));
    }
    toast.success(`Diff exported as ${format.toUpperCase()}`);
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="size-4 text-primary" />
            Chunk diff — {item?.slug}
          </DialogTitle>
          <DialogDescription>
            {item ? (
              <>
                {item.prev_chunks} → {item.chunks} chunks ·{" "}
                {item.embedding_meta?.model ?? "—"} · dims {item.embedding_meta?.dims ?? "—"}
                {Array.isArray(item.embedding_meta?.sample_vector) && (
                  <> · sample [{item.embedding_meta!.sample_vector!.map((v) => v.toFixed(3)).join(", ")}…]</>
                )}
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {item && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => exportDiff("json")}>
              <FileDown className="size-3.5" /> Export JSON
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportDiff("csv")}>
              <FileDown className="size-3.5" /> Export CSV
            </Button>
          </div>
        )}


        {maxLen === 0 ? (
          <p className="text-sm text-muted-foreground">No chunk preview captured for this item.</p>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => {
              const changed = (r.p?.hash ?? "") !== (r.n?.hash ?? "");
              return (
                <div key={r.ordinal} className={`rounded border p-2 ${changed ? "border-amber-500/50" : "border-border"}`}>
                  <div className="mb-1 flex items-center justify-between font-mono text-[10px] text-muted-foreground">
                    <span>chunk #{r.ordinal}</span>
                    <span>
                      {r.p ? `prev ${r.p.hash} (${r.p.chars}c)` : "—"} → {r.n ? `new ${r.n.hash} (${r.n.chars}c)` : "—"}
                      {changed && <span className="ml-2 text-amber-500">changed</span>}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] font-mono">
                    <pre className="rounded bg-muted/40 p-2 whitespace-pre-wrap break-words">
                      {r.p?.preview ?? <span className="text-muted-foreground">(new chunk — no previous)</span>}
                    </pre>
                    <pre className="rounded bg-muted/40 p-2 whitespace-pre-wrap break-words">
                      {r.n?.preview ?? <span className="text-muted-foreground">(removed — no replacement)</span>}
                    </pre>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
